import React, { memo, useCallback, useEffect, useRef } from "react";
import { IDashboardContext, useDashboardContext } from "../../contexts/dashboard-context";
import { usePreviewContext } from "../../contexts/preview-context";
import { useBottomMenu } from "../../hooks/useBottomMenu";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { useMessage } from "../../hooks/useMessage";
import { useSelectedZone } from "../../hooks/useSelection";
import { useStableCallback } from "../../hooks/useStableCallback";
import { transformToFrame } from "../../transform";
import { BottomMenuPaths } from "../bottom-menu/paths";
import { IPanCropOverlayRef, PanCropOverlay } from "./pan-crop-overlay";

function canEnableTransformMode(
	dashboard: IDashboardContext,
	selectedZone: eXpress.core.IZone | undefined,
) {
	return dashboard.canPanPhoto(selectedZone) || dashboard.canZoomPhoto(selectedZone);
}

/** Overlay on top of Preview that we can use to draw extra elements during photo panning/zooming */
export const PanCropOverlayContainer = memo(() => {
	/** Ref to DOM element */
	const panCropOverlayRef = useRef<IPanCropOverlayRef>(null);
	const selectedZone = useSelectedZone();
	const previewContext = usePreviewContext();
	const { slideShowPreviewRef } = usePreviewContext();
	const [isTransformMode, setIsTransformMode] = useIsTransformMode();
	const dashboard = useDashboardContext();
	const { gotoPath } = useBottomMenu();

	const updateOverlay = useCallback(
		function updateOverlay() {
			const panCropOverlay = panCropOverlayRef.current;
			if (!panCropOverlay) return;

			// Update when TransformMode changes
			const screenToSpreadDipTransform = previewContext.getScreenToSpreadTransform(
				selectedZone?.spread,
				undefined,
			);
			const photoLookZone = eXpress.core.isPhotoLookZone(selectedZone)
				? selectedZone
				: undefined;
			if (isTransformMode && screenToSpreadDipTransform && photoLookZone) {
				panCropOverlay.updateOverlay(
					photoLookZone,
					transformToFrame(screenToSpreadDipTransform),
				);
			} else {
				panCropOverlay.updateOverlay(undefined);
			}
		},
		[isTransformMode, previewContext, selectedZone],
	);

	// We can use useMessage because we don't depend on react state
	useMessage(
		useStableCallback((message) => {
			if (
				eXpress.core.isPhotoSizeOrPositionChangedInZone(message) &&
				message.affects(selectedZone)
			) {
				updateOverlay();
			}
		}),
		{ captureInteractiveMessages: true },
	);

	useEffect(() => {
		// Disable Transform mode if we cannot do it on the selected zone
		if (!canEnableTransformMode(dashboard, selectedZone)) {
			setIsTransformMode(false);
			// BottomMenu: No need to update if zone selection is changed
			// It is updated to the default BottomMenu of that zone
		}

		// Update on TransformMode state change
		updateOverlay();

		// Follow preview, listen to Preview Zoom/Pinches
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return undefined;
		const unsubscribe = slideShow.onTransformChange.subscribe(() => {
			updateOverlay();
		});

		return () => {
			unsubscribe();
		};
	}, [
		dashboard,
		selectedZone,
		setIsTransformMode,
		slideShowPreviewRef,
		updateOverlay,
		isTransformMode,
	]);

	useMessage(
		useCallback(
			(message) => {
				// Example undo of filling a photoZone
				// The SwapPhotos commando handles the zone selection behaviour (isSwapPhotosMessage == false => skip)
				if (
					eXpress.core.isPhotoInExistingZoneChanged(message) &&
					!eXpress.core.isSwapPhotosMessage(message)
				) {
					if (!canEnableTransformMode(dashboard, selectedZone)) {
						if (!isTransformMode) return;
						setIsTransformMode(false);
						gotoPath(BottomMenuPaths.PhotoSelection);
					}
				}
			},
			[dashboard, gotoPath, isTransformMode, selectedZone, setIsTransformMode],
		),
	);

	return <PanCropOverlay ref={panCropOverlayRef} />;
});
PanCropOverlayContainer.displayName = "PanCropOverlayContainer";
